import { useEffect } from 'react';
import { useParams, Navigate, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { usePortalTenantScopeStore } from '@/store/portal-tenant-scope.store';
import { usePortalAuthStore } from '@/store/portal-auth.store';
import { Skeleton } from '@/components/ui/skeleton';

interface TenantSummaryData {
  tenantId: string;
  tenantSlug: string;
  assessmentCounts: Record<string, number>;
  certificationCount: number;
}

// Layout route that resolves :tenantSlug → tenantId via summary API call.
// On success: populates portal-tenant-scope store + renders <Outlet />.
// On 404/error: redirects to /portal/home.
// Renders loader while resolving.

export function TenantScopeProvider() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { setScope, clearScope } = usePortalTenantScopeStore();
  const { selectTenant } = usePortalAuthStore();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['portal-tenant-scope', tenantSlug],
    queryFn: async () => {
      const { data } = await apiClient.get(`/portal/tenants/${encodeURIComponent(tenantSlug!)}/summary`);
      return data.data as TenantSummaryData & { tenantName?: string };
    },
    enabled: !!tenantSlug,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (data) {
      setScope(tenantSlug!, data.tenantId, data.tenantName ?? tenantSlug!);
      // FN-TR-4: populate portal-auth.store.selectedTenantId from slug-resolved context
      // so the API interceptor sends X-Vendor-Tenant-Id on portal requests.
      // Called on the resolution EVENT (not relying on persisted scope) so a reload
      // that re-runs slug resolution re-populates selectedTenantId correctly.
      selectTenant(data.tenantId);
    }
    return () => { clearScope(); };
  }, [data, tenantSlug, setScope, clearScope, selectTenant]);

  if (!tenantSlug) return <Navigate to="/portal/home" replace />;
  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (isError) return <Navigate to="/portal/home" replace />;

  return <Outlet />;
}
